import UpLoadCSVIcon from '@/component/ticketKnowledge/icons/UpLoadCSVIcon';
import DownloadCsvIcon from '@/component/ticketKnowledge/icons/DownloadCsvIcon';
import { Tooltip, Button } from '@material-ui/core';
import I18n from '@/rI18n';
import { useEffect, useState } from 'react';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import ToolActionResultDialog from './ToolActionResultDialog';
import UploadModal from './UploadModal';
import Utils from '@/common/utils/utils';
import PropTypes from 'prop-types';
import { Storage } from 'aws-amplify';
import { uploadFileToS3 } from '@/service/s3Service';
import { useRecoilValue, useRecoilState } from 'recoil';
import { projectInuseDetailAtom } from '@/state/atom/projectAtom';
import { corporationAtom } from '@/state/atom/corporationAtom';
import { userAtom } from '@/state/atom/userAtom';
import { useLocation } from 'react-router';
import { 
    bulkActionTagItemsTaskIdAtom,
} from '@/state/atom/ticketKnowledgeAtom';
import Constant from '@/common/constant/Constant';
import DataChangePleaseReloadModal from '@/component/common/modal/DataChangePleaseReloadModal';
import { createNewTask } from '@/service/taskService';

/**
 * ExportUploader component for handling CSV download/upload actions and log viewing.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.displayDownloader - Show download button
 * @param {boolean} props.disableDownloader - Disable download button
 * @param {boolean} props.displayUploader - Show upload button
 * @param {boolean} props.disableUploader - Disable upload button
 * @param {boolean} props.displayLogger - Show log button
 * @param {boolean} props.disableLogger - Disable log button
 * @param {function} props.downloadHandler - Handler for download action
 */
const ExportUploader = ({
    displayDownloader,
    disableDownloader,
    displayUploader,
    disableUploader,
    displayLogger,
    disableLogger,
    targetUploadId,
    downloadHandler,
    uploadHandler,
}) => {

    const location = useLocation();
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        isOpen: false,
        message: ''
    });
    const [isOpenUploadModal, setOpenUploadModal] = useState(false);
    const [isOpenDownloadPopup, setOpenDownloadPopup] = useState(false);
    const [isOpenModalReloadPage, setIsOpenModalReloadPage] = useState(false);
    const [uploadFile, setUploadFile] = useState();
    const projectInuseDetail = useRecoilValue(projectInuseDetailAtom);
    const corporationDetail = useRecoilValue(corporationAtom);
    const userDetail = useRecoilValue(userAtom);
    const [downloadUploadTagItemsId , setDownloadUploadTagItemsId ] = useRecoilState(bulkActionTagItemsTaskIdAtom);
    const [action , setAction ] = useState();

    useEffect(() => {
        return () => {
            setDownloadUploadTagItemsId('');
        };
    }, [setDownloadUploadTagItemsId]);

    useEffect(() => {
        // set action loading when uploading
        if (!Utils.isEmptyString(downloadUploadTagItemsId)) {
            setIsActionLoading(true);
        } else {
            setIsActionLoading(false);
        }
    }, [downloadUploadTagItemsId]);

    const viewLogsHandler = () => {
        let locationPath = `${location.pathname}/log-screen?object=tagitem&objectId=${targetUploadId}`;
        const win = window.open(locationPath, "_blank");
        win.focus();
    };

    const onClickDownload = () => {
        setOpenDownloadPopup(true);
    };

    const onClickUpload = () => {
        setOpenUploadModal(true);
    };

    const errorConfirmSubmit = () => {
        setErrorMessage({
            message: '',
            isOpen: false
        });
    };

    const closeUploadModalHandler = () => {
        setOpenUploadModal(false);
        setUploadFile({});
    };

    const isDisableUploadButton = () => {
        return Utils.isEmptyObject(uploadFile);
    };

    const onClickDownloadHandler = () => {
        setOpenDownloadPopup(false);
        setErrorMessage({
            isOpen: false,
            message: ''
        });
        setAction(Constant.DOWNLOAD_UPLOAD_METHOD.DOWNLOAD)
        setIsActionLoading(true);
        downloadHandler()
            .then(async data => {
                if (!data?.dataDownloadUploader?.status && data?.dataDownloadUploader?.message === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                    setDownloadUploadTagItemsId('');
                    setIsOpenModalReloadPage(true);
                    return;
                }

                if (
                    !Utils.isEmptyObject(data?.dataDownloadUploader) &&
                    !data?.dataDownloadUploader?.status
                ) {
                    return setErrorMessage({
                        isOpen: true,
                        message: I18n.get(
                            data?.dataDownloadUploader?.message ||
                                'MSG-CM-OTHERS-ERROR-WHEN-EXPORT-CSV'
                        )
                    });
                }
                const downloadFileUrl = data?.dataDownloadUploader?.downloadResponseDto?.downloadFileUrl.replaceAll(
                    'public/',
                    ''
                );
                await triggerDownloadFile(downloadFileUrl);
            })
            .catch(error => {
                setErrorMessage({
                    isOpen: true,
                    message: I18n.get('MSG-CM-DISCONNECT-NETWORK-WHEN-EXPORT-CSV')
                });
            })
            .finally(() => {
                setIsActionLoading(false);
            });
    };

    const triggerDownloadFile = async url => {
        let fileName = url?.split('/').pop();
        fileName = decodeURIComponent(fileName);
        const exportCsvUrl = await Storage.get(url, { level: 'public' });
        const exportCsvRes = await fetch(exportCsvUrl);
        const exportCsvBlob = await exportCsvRes.blob();
        const exportCsvDownloadUrl = window.URL.createObjectURL(new Blob([exportCsvBlob]));
        const downloadLink = document.createElement('a');
        downloadLink.href = exportCsvDownloadUrl;
        downloadLink.setAttribute('download', fileName);
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    };

    const onClicUploadHandler = async () => {
        setOpenUploadModal(false);
        setErrorMessage({
            isOpen: false,
            message: ''
        });
        setIsActionLoading(true);
        setAction(Constant.DOWNLOAD_UPLOAD_METHOD.UPLOAD)
        //upload file to s3 folder
        const uploadFileS3Path = `tmp/import/${projectInuseDetail.id}/${uploadFile?.name}`;
        const uploadFileData = {
            file: uploadFile,
            key: uploadFile?.name
        };
        const uploadedS3File = await uploadFileToS3(uploadFileS3Path, uploadFileData);
        
        //create task for upload process
        const uploadTask = await createNewTask({
            usage: Constant.TASK_USAGE.BULK_UPLOAD_TAG,
            type: Constant.TASK_TYPE.BULK_UPLOAD_TAG,
            status: Constant.TASK_STATUS.START,
            info: JSON.stringify({
                bulkActionType: Constant.BULK_ACTION.DOWNLOAD_UPLOAD_TAG_ITEMS,
                uploadTotal: 0
            }),
            taskId: ""
        });
        setDownloadUploadTagItemsId(uploadTask?.id)
        //request import.
        uploadHandler({
            corporationInuseId: corporationDetail?.id,
            projectInuseId: projectInuseDetail?.id,
            userInuseId: userDetail?.id,
            targetUploadId,
            uploadFileUrl: uploadedS3File?.key,
            taskId: uploadTask?.id
        })
            .then(data => {
                if (!data?.dataDownloadUploader?.status && data?.dataDownloadUploader?.message === Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR) {
                    setDownloadUploadTagItemsId('');
                    setIsOpenModalReloadPage(true);
                    return;
                }
            })
            .catch(err => {
                console.log("err", err)
                setErrorMessage({
                    isOpen: true,
                    message: I18n.get('MSG-CM-OTHERS-ERROR-WHEN-EXPORT-CSV')
                });
                setIsActionLoading(false);
            })
            .finally(() => {
                //reset upload file state
                setUploadFile({});
            });
    };

    return (
        <>
            <div className="tools-group d-flex justify-content-start align-items-center gap-3">
                {displayDownloader && (
                    <Tooltip title={I18n.get('DOWNLOAD-CSV-FORMAT')} placement="top-start" arrow>
                        <Button
                            className="tool-items"
                            variant="text"
                            onClick={() => onClickDownload()}
                            disabled={disableDownloader}
                        >
                            <DownloadCsvIcon width={18} height={20} />
                        </Button>
                    </Tooltip>
                )}

                {displayUploader && (
                    <Tooltip title={I18n.get('UPLOAD-CSV-FORMAT')} placement="bottom-start" arrow>
                        <Button
                            className="tool-items"
                            variant="text"
                            onClick={() => onClickUpload()}
                            disabled={disableUploader}
                        >
                            <UpLoadCSVIcon width={18} height={20} />
                        </Button>
                    </Tooltip>
                )}

                {displayLogger && (
                    <Button
                        className="tool-items"
                        variant="text"
                         onClick={() => viewLogsHandler()}
                        disabled={disableLogger}
                    >
                        <img
                            className="option-icon"
                            src="/assets/image/process-upload-csv.svg"
                            alt="icon-view-log"
                        />
                    </Button>
                )}

                {isActionLoading && (
                    <div className="tool-items">
                        <Tooltip
                            title={action === Constant.DOWNLOAD_UPLOAD_METHOD.UPLOAD ? I18n.get('MSG-CM-TOOLTIP-WHEN-HOVER-ICON-UPLOADING') : I18n.get('MSG-CM-TOOLTIP-WHEN-HOVER-ICON-LOADING')}
                            placement="bottom-start"
                            arrow
                        >
                            <span className="tool-items">
                                <img
                                    className="option-icon icon-loading"
                                    src={`/assets/image/icon-loading.svg`}
                                    alt="icon-loading"
                                />
                            </span>
                        </Tooltip>
                    </div>
                )}
            </div>

            <ConfirmDialog
                isOpen={errorMessage.isOpen}
                msgConfirm={errorMessage.message}
                okCallBack={() => errorConfirmSubmit()}
            />

            <ToolActionResultDialog
                isOpen={false}
                toolActionResult={{}}
                submitHandler={() => {}}
                viewLogsHandler={() => viewLogsHandler()}
            />

            <UploadModal
                isOpen={isOpenUploadModal}
                closeHandler={closeUploadModalHandler}
                isDisable={isDisableUploadButton()}
                uploadFile={uploadFile}
                setUploadFile={setUploadFile}
                uploadHandler={onClicUploadHandler}
            />

            <ConfirmDialog
                isOpen={isOpenDownloadPopup}
                msgConfirm={I18n.get('MSG-CM-CONFIRM-EXPORT-CSV')}
                closeCallBack={() => setOpenDownloadPopup(false)}
                okCallBack={() => onClickDownloadHandler()}
                textSubmit={I18n.get('SUBMIT')}
                textClose={I18n.get('COMBTN001')}
                disableButtonCallBack={false}
            />

            <DataChangePleaseReloadModal
                isOpen={isOpenModalReloadPage}
                setIsOpen={setIsOpenModalReloadPage}
            />
        </>
    );
};

ExportUploader.propTypes = {
    displayDownloader: PropTypes.bool,
    disableDownloader: PropTypes.bool,
    displayUploader: PropTypes.bool,
    disableUploader: PropTypes.bool,
    displayLogger: PropTypes.bool,
    disableLogger: PropTypes.bool,
    downloadHandler: PropTypes.func.isRequired
};

export default ExportUploader;
